import {queryOptions} from '@tanstack/react-query'
import {notFound} from '@tanstack/react-router'
import {createServerFn} from '@tanstack/react-start'
import {compact} from 'lodash-es'
import {z} from 'zod'
import fetchDesignCollections from '~/client/spreadshop/old/fetchDesignCollections'
import fetchVisibleCollections from '~/client/sanity/fetchVisibleCollections'
import {applyServerFnCache} from '~/client/fn/utils'
import {stringToNumber} from '~/utils/number'
import {shuffleBySeed} from '~/utils/random'
import {Locale} from '~/gql/graphql'

const CACHE_TIME_SEC = 60 * 60 * 3 // 3h

const collectionsParams = z
    .object({
        locale: z.nativeEnum(Locale).optional(),
    })
    .optional()

const getCollections = createServerFn({
    method: 'GET',
})
    .inputValidator((data: unknown) => {
        const result = collectionsParams.safeParse(data)
        if (result.error) {
            throw notFound()
        }

        return result.data
    })
    .handler(async ({data}) => {
        const {locale} = data ?? {}
        const [designCollections, visibleCollections] = await Promise.all([
            fetchDesignCollections(locale),
            fetchVisibleCollections(locale),
        ])

        const collections = visibleCollections.map((collectionDetails) => {
            const designCollection = designCollections.items.find(
                (collection) => collection.id === collectionDetails.collection.id
            )

            if (!designCollection) {
                return undefined
            }

            return {
                ...designCollection,
                designs: shuffleBySeed(
                    designCollection.designs,
                    stringToNumber(designCollection.id)
                ),
                name: collectionDetails.overwrites?.title ?? designCollection.name,
                description:
                    collectionDetails.overwrites?.description ?? designCollection.description,
            }
        })

        applyServerFnCache(CACHE_TIME_SEC)

        return compact(collections)
    })

export const collectionsQueryOptions = (locale: Locale = Locale.EnEu) =>
    queryOptions({
        queryKey: [locale, 'collections'],
        queryFn: () => getCollections({data: {locale}}),
        gcTime: 1000 * CACHE_TIME_SEC, // 3h
        staleTime: 1000 * CACHE_TIME_SEC, // 3h
    })
